import {
  Box,
  Flex,
  Heading,
  Image,
  Text,
  Circle,
  useColorModeValue,
  // eslint-disable-next-line no-restricted-imports
  Icon as ChakraIcon,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';
import { useMemo } from 'react';
import stepsConnectorLineLight from 'src/assets/images/vertical-mid-steps-line-light.svg';
import stepsConnectorLineDark from 'src/assets/images/vertical-mid-steps-line.svg';
import { Button } from 'src/components/atoms/Button';
import { stepActiveGradient, stepDoneGradient } from 'src/theme';

const steps = [
  { label: 'Create Account' },
  { label: 'Connect channels' },
  { label: 'Upload data' },
  { label: 'Start Modeling' },
  { label: 'Invite colleagues' },
];

const CIRCLE_SIZE = '50px';

type StepperSidebarProps = {
  title: string;
  subtitle: string;
  currentStep: number;
  onNext?: () => void;
  onBack?: () => void;
  isNextDisabled?: boolean;
  isBackDisabled?: boolean;
  isFormValid?: boolean;
  nextLabel?: string;
};

const StepCircle = ({
  index,
  isCompleted,
  isCurrent,
}: {
  index: number;
  isCompleted: boolean;
  isCurrent: boolean;
}) => {
  if (isCompleted) {
    return (
      <Circle
        size={CIRCLE_SIZE}
        bg={stepDoneGradient}
        border="2px solid"
        borderColor="stepper.done"
        zIndex={1}>
        <ChakraIcon as={Check} boxSize="24px" color="stepper.done" />
      </Circle>
    );
  }

  if (isCurrent) {
    // Per Figma: the circle keeps its own grey border, and the gradient ring
    // is a detached outer halo separated by a gap of page background.
    // CSS borders can't take gradients on a circle, so each ring is a padded
    // wrapper: gradient (2px) -> page-bg gap (3px) -> grey-bordered circle.
    return (
      <Box borderRadius="full" p="2px" bg={stepActiveGradient} zIndex={1}>
        <Box borderRadius="full" p="3px" bg="surface.page">
          <Circle
            size={CIRCLE_SIZE}
            bg="stepper.circleBg"
            border="2px solid"
            borderColor="stepper.upcoming"
            color="content.body"
            fontWeight={500}
            fontSize="24px">
            {index + 1}
          </Circle>
        </Box>
      </Box>
    );
  }

  return (
    <Circle
      size={CIRCLE_SIZE}
      bg="stepper.circleBg"
      border="2px solid"
      borderColor="stepper.upcoming"
      color="stepper.textMuted"
      fontWeight={500}
      fontSize="24px"
      zIndex={1}>
      {index + 1}
    </Circle>
  );
};

export const StepperSidebar: React.FC<StepperSidebarProps> = ({
  title,
  subtitle,
  currentStep,
  onNext,
  onBack,
  isNextDisabled,
  isBackDisabled,
  isFormValid = true,
  nextLabel,
}) => {
  const displayStep = useMemo(() => {
    // currentStep values and mapping:
    // 0 -> 0: Create Account -> Create Account (step 0 in sidebar)
    // 1 -> 1: Connect Channels -> Connect Channels (step 1 in sidebar)
    // 2 -> 2: Upload Data -> Admit Data (step 2 in sidebar)
    // 3 -> 2: Preparing Data -> Admit Data (step 2 in sidebar, collapsed)
    // 4 -> 2: Campaign Match -> Admit Data (step 2 in sidebar, collapsed)
    // 5 -> 3: Start Modeling -> Start Modeling (step 3 in sidebar)
    // 6 -> 4: Invite Colleagues -> Invite Colleagues (step 4 in sidebar)
    if (currentStep === 0) return 0; // Create Account
    if (currentStep === 1) return 1; // Connect Channels
    if (currentStep >= 2 && currentStep <= 4) return 2; // Upload, Preparing, Campaign Match -> Admit Data
    if (currentStep >= 5) return currentStep - 2; // Start Modeling and beyond (5->3, 6->4)
    return currentStep;
  }, [currentStep]);

  const buttonName = useMemo(() => {
    if (displayStep === 0) return 'Create account';
    return nextLabel ?? 'Next';
  }, [displayStep, nextLabel]);

  const connectorSrc = useColorModeValue(
    stepsConnectorLineLight,
    stepsConnectorLineDark,
  );
  const backHoverBg = useColorModeValue(
    'background.light.hover',
    'whiteAlpha.100',
  );

  return (
    <Flex direction="column" h="100%">
      <Heading
        as="h1"
        size="lg"
        mb={3}
        color="content.title"
        fontWeight={700}
        fontFamily="marketingPage">
        {title}
      </Heading>
      <Text color="content.muted" mb={8} fontSize="16px">
        {subtitle}
      </Text>

      <Box flex={1}>
        {steps.map((step, idx) => {
          const isCompleted = idx < displayStep;
          const isCurrent = idx === displayStep;
          return (
            <Flex
              key={step.label}
              align="center"
              minH="86px"
              position="relative">
              <Flex
                w={CIRCLE_SIZE}
                direction="column"
                align="center"
                position="relative"
                alignSelf="stretch"
                justify="center">
                {/* Dashed connector with top fade, centered between this and
                    the previous circle */}
                {idx > 0 && (
                  <Image
                    src={connectorSrc}
                    alt=""
                    position="absolute"
                    left="50%"
                    transform="translateX(-50%)"
                    top="-18px"
                    h="36px"
                    w="1.5px"
                  />
                )}
                <StepCircle
                  index={idx}
                  isCompleted={isCompleted}
                  isCurrent={isCurrent}
                />
              </Flex>
              <Text
                ml={4}
                fontWeight={isCurrent ? 700 : 500}
                color={
                  isCurrent || isCompleted
                    ? 'content.title'
                    : 'stepper.textMuted'
                }
                fontSize="16px">
                {step.label}
              </Text>
            </Flex>
          );
        })}
      </Box>

      <Flex pt={6} gap={4}>
        {displayStep !== 0 && displayStep !== 1 && (
          <Button
            flex={1}
            buttonName="Back"
            bg="surface.panel"
            border="1px solid"
            borderColor="surface.border"
            color="content.body"
            borderRadius="lg"
            py={3.5}
            fontWeight={600}
            fontSize="lg"
            boxShadow="none"
            _hover={{ bg: backHoverBg }}
            onClick={onBack}
            isDisabled={isBackDisabled}>
            Back
          </Button>
        )}
        <Button
          flex={1}
          buttonName={buttonName}
          bg="icon.green"
          color="white"
          borderRadius="lg"
          py={3.5}
          fontWeight={600}
          fontSize="lg"
          boxShadow="none"
          _hover={{ bg: 'icon.green', opacity: 0.9 }}
          onClick={onNext}
          isDisabled={displayStep === 0 ? !isFormValid : isNextDisabled}>
          {buttonName}
        </Button>
      </Flex>
    </Flex>
  );
};
